import { z } from 'zod';
import { type FieldDef, multiValues, type ObjectDef, writableFields } from './objects';

const uuid = z.string().uuid();
/** A real calendar day as YYYY-MM-DD (2026-13-01 and 2026-02-31 are refused). */
export const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(v);
  }, 'Enter a real calendar date');

/** Zod schema for one writable field. Empty strings are normalised to null. */
export function fieldSchema(f: FieldDef): z.ZodTypeAny {
  let s: z.ZodTypeAny;
  switch (f.type) {
    case 'text':
    case 'textarea':
    case 'phone':
      s = z.string().trim().max(f.maxLength ?? 255);
      break;
    case 'email':
      s = z.string().trim().toLowerCase().email('Enter a valid email address').max(f.maxLength ?? 255);
      break;
    case 'url':
      s = z.string().trim().max(f.maxLength ?? 255).regex(/^(https?:\/\/)?[^\s/$.?#].[^\s]*$/i, 'Enter a valid web address');
      break;
    case 'number':
      s = z.number().int().min(0).max(2_147_483_647);
      break;
    case 'currency':
      // Capped so values stay exact as JS numbers (numeric(18,2) in Postgres). Never negative: a negative deal amount
      // would subtract from the pipeline, the forecast and the CEO figures.
      s = z.number().finite().min(0, 'Enter an amount of 0 or more').max(1e13);
      break;
    case 'percent':
      s = z.number().min(0).max(100);
      break;
    case 'date':
      s = isoDate;
      break;
    case 'boolean':
      s = z.boolean();
      break;
    case 'picklist': {
      const values = (f.options ?? []).filter((o) => !o.inactive).map((o) => o.value);
      s = z.string().refine((v) => values.includes(v), { message: `Choose one of: ${values.join(', ')}` });
      break;
    }
    case 'multipicklist': {
      // A list of values, or 'a;b' text; stored as ';'-separated keys in list order, without repeats.
      const all = f.options ?? [];
      const active = all.filter((o) => !o.inactive).map((o) => o.value);
      s = z
        .union([z.array(z.string().max(80)).max(50), z.string().max(f.maxLength ?? 2000)])
        .transform((v) => [...new Set(multiValues(v))])
        .refine((v) => v.every((x) => active.includes(x)), { message: `Choose from: ${active.join(', ')}` })
        .transform((v) => (v.length ? all.filter((o) => v.includes(o.value)).map((o) => o.value).join(';') : null));
      break;
    }
    case 'reference':
    case 'user':
    case 'stage':
    case 'lookup':
      s = uuid;
      break;
    case 'datetime':
      s = z.string().datetime();
      break;
  }
  const emptyToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);
  return z.preprocess(emptyToNull, f.required ? s : s.nullable());
}

/** Schema for create (required fields enforced) or update (all optional, partial). */
export function recordInputSchema(obj: ObjectDef, mode: 'create' | 'update') {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of writableFields(obj)) {
    const fs = fieldSchema(f);
    // ownerId, stage and lead status are defaulted server-side on create, so they are optional in input.
    // Deal source is asked for in the form, but the API and imports may leave it out (older deals have none).
    const serverDefault = f.name === 'ownerId' || f.type === 'stage' || (obj.name === 'lead' && f.name === 'status') || (obj.name === 'opportunity' && f.name === 'dealSource');
    const optional = mode === 'update' || !f.required || serverDefault;
    shape[f.name] = optional ? fs.optional() : fs;
  }
  return z.object(shape).strict();
}

export const FILTER_OPS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'contains', 'startsWith', 'in', 'isNull', 'notNull'] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

export const filterSchema = z.object({
  field: z.string().min(1).max(64),
  op: z.enum(FILTER_OPS),
  value: z.unknown().optional(),
});
export type Filter = z.infer<typeof filterSchema>;

export const listQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  sort: z.string().max(64).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
  /** JSON-encoded Filter[] */
  filter: z
    .string()
    .max(4000)
    .optional()
    .transform((s, ctx) => {
      if (!s) return [] as Filter[];
      try {
        return z.array(filterSchema).max(20).parse(JSON.parse(s));
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'filter must be a JSON array of {field, op, value}' });
        return z.NEVER;
      }
    }),
  mine: z.enum(['true', 'false']).optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  password: z.string().min(1).max(256),
  /** "Keep me signed in": stay signed in for 30 days; otherwise until the browser (or app) is closed. */
  remember: z.boolean().optional(),
});

export const signupSchema = z.object({
  organizationName: z.string().trim().min(2).max(120),
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email().max(255),
  password: z
    .string()
    .min(8, 'Use at least 8 characters')
    .max(256),
});
